import { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Select, MenuItem, usePagination } from '@mui/material';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import './SupportHelp.css';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { SupportHelpTable } from '../../../../components/Admin/Payments/SupportHelp/SupportHelpTable';

const formatText = text => {
  if (!text) return text;
  return text
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const SupportHelpContent = ({
  searchTerm = '',
  appliedSearchTerm = '',
  appliedStatus = 'all',
  appliedCategory = 'all',
}) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (appliedStatus !== 'all') params.status = appliedStatus;
      if (appliedCategory !== 'all') params.category = appliedCategory;
      if (appliedSearchTerm && appliedSearchTerm.trim()) {
        params.search_query = appliedSearchTerm.trim();
      }

      const { data } = await axiosInstance.get('/admin/support-and-help', {
        params,
      });

      setTickets(Array.isArray(data?.results) ? data.results : []);
      setTotalCount(typeof data?.count === 'number' ? data.count : 0);
    } catch (err) {
      setTickets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, appliedStatus, appliedCategory, appliedSearchTerm]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = event => {
    setPageSize(event.target.value);
    setPage(1);
  };

  const { items } = usePagination({
    count: totalPages,
    page: page,
    onChange: handlePageChange,
  });

  return (
    <Box className="flex flex-col gap-4">
      <SupportHelpTable data={tickets} isLoading={loading} />

      <Box className="supporthelp-pagination-container" sx={{ marginTop: 0, paddingTop: 0 }}>
        <Box className="supporthelp-pagination-info">
          <Typography className="supporthelp-pagination-text">Showing</Typography>
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="supporthelp-pagination-select"
            sx={{
              height: '30px',
              minWidth: '20px',
              '& .MuiSelect-select': {
                padding: '6px 4px 4px 8px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
          </Select>
          <Typography className="supporthelp-pagination-text">
            of {totalCount} support tickets
          </Typography>
        </Box>
        <nav>
          <ul className="supporthelp-pagination-nav">
            {items.map(({ page: itemPage, type, selected, ...item }, index) => {
              let children = null;

              if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                children = <span className="supporthelp-pagination-ellipsis">…</span>;
              } else if (type === 'page') {
                children = (
                  <button
                    type="button"
                    className={`supporthelp-pagination-button ${selected ? 'selected' : ''}`}
                    {...item}
                  >
                    {itemPage}
                  </button>
                );
              } else {
                children = (
                  <button type="button" className="supporthelp-pagination-button" {...item}>
                    {formatText(type)}
                  </button>
                );
              }
              return <li key={index}>{children}</li>;
            })}
          </ul>
        </nav>
      </Box>
    </Box>
  );
};

const SupportHelp = () => {
  const [activeTab, setActiveTab] = useState(1);
  const navigate = useNavigate();

  const handleTabChange = (event, newValue) => {
    if (newValue === 0) {
      navigate('/admin/payments/disputes');
    } else {
      setActiveTab(newValue);
    }
  };

  return (
    <Layout title="Klass Online | Support & Dispute">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] relative bg-[#F9F9F9] rounded-lg mr-[10px] flex flex-col overflow-y-auto">
          <Box className="flex flex-col p-[1rem] gap-3">
            <Box className="flex justify-between items-center">
              <Typography className="request-title">Support & Dispute</Typography>
            </Box>

            <Box>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 36 }}>
                <Tab label="Dispute" sx={{ textTransform: 'none', minHeight: 36 }} />
                <Tab label="Support & Help" sx={{ textTransform: 'none', minHeight: 36 }} />
              </Tabs>
            </Box>

            {activeTab === 1 && <SupportHelpContent />}
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default SupportHelp;
